import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { webauthnAPI, authAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  UserCircle, Mail, Shield, Calendar, Fingerprint, Plus, Trash2, 
  Key, Laptop, AlertTriangle, Lock, Eye, EyeOff 
} from 'lucide-react';
import { ROLE_COLORS, ROLE_LABELS } from '@/components/AppLayout';

// Helper to convert base64url to Uint8Array
function base64urlToUint8Array(base64url) {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padding = '='.repeat((4 - base64.length % 4) % 4);
  const binary = atob(base64 + padding);
  return Uint8Array.from(binary, c => c.charCodeAt(0));
}

// Helper to convert ArrayBuffer to base64url
function arrayBufferToBase64url(buffer) {
  const binary = String.fromCharCode.apply(null, new Uint8Array(buffer));
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export default function ProfilePage() {
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [credentials, setCredentials] = useState([]);
  const [loadingCredentials, setLoadingCredentials] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [deviceName, setDeviceName] = useState('');
  
  // 2FA State
  const [twoFAEnabled, setTwoFAEnabled] = useState(true);
  const [toggling2FA, setToggling2FA] = useState(false);
  
  // Trusted Devices State
  const [trustedDevices, setTrustedDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(true);
  
  // Password Change State
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Delete Account State
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  useEffect(() => {
    checkBiometricAvailability();
    loadCredentials();
    load2FAStatus();
    loadTrustedDevices();
    // Refresh user data on page load
    checkAuth();
  }, []);

  const checkBiometricAvailability = async () => {
    if (window.PublicKeyCredential) {
      try {
        const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
        setBiometricAvailable(available);
      } catch (e) {
        setBiometricAvailable(false);
      }
    }
  };

  const loadCredentials = async () => {
    try {
      const res = await webauthnAPI.getCredentials();
      setCredentials(res.data);
    } catch (err) {
      setCredentials([]);
    } finally {
      setLoadingCredentials(false);
    }
  };

  const load2FAStatus = async () => {
    try {
      const res = await authAPI.get2FAStatus();
      setTwoFAEnabled(res.data.two_fa_enabled);
    } catch (err) {
      // Use user data as fallback
      setTwoFAEnabled(user?.two_fa_enabled ?? true);
    }
  };

  const loadTrustedDevices = async () => {
    try {
      const res = await authAPI.getTrustedDevices();
      setTrustedDevices(res.data);
    } catch (err) {
      setTrustedDevices([]);
    } finally {
      setLoadingDevices(false);
    }
  };

  const handleToggle2FA = async (enabled) => {
    setToggling2FA(true);
    try {
      await authAPI.toggle2FA(enabled);
      setTwoFAEnabled(enabled);
      toast.success(`2FA ${enabled ? 'abilitato' : 'disabilitato'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel cambio 2FA');
    } finally {
      setToggling2FA(false);
    }
  };

  const handleRemoveTrustedDevice = async (deviceId) => {
    try {
      await authAPI.removeTrustedDevice(deviceId);
      toast.success('Dispositivo rimosso');
      loadTrustedDevices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella rimozione');
    }
  };

  const handleRemoveAllTrustedDevices = async () => {
    if (!window.confirm('Rimuovere tutti i dispositivi attendibili?')) return;
    try {
      await authAPI.removeAllTrustedDevices();
      toast.success('Tutti i dispositivi rimossi');
      localStorage.removeItem('trusted_device_token');
      loadTrustedDevices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella rimozione');
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) {
      toast.error('Compila tutti i campi');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('La nuova password deve avere almeno 6 caratteri');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Le password non corrispondono');
      return;
    }
    
    setChangingPassword(true);
    try {
      await authAPI.changePassword(currentPassword, newPassword);
      toast.success('Password cambiata con successo');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel cambio password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword && user?.password_hash !== null) {
      toast.error('Inserisci la password per confermare');
      return;
    }
    
    setDeletingAccount(true);
    try {
      await authAPI.deleteAccount(deletePassword, 'DELETE');
      toast.success('Account eliminato');
      logout();
      navigate('/login', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nell\'eliminazione');
    } finally {
      setDeletingAccount(false);
      setShowDeleteDialog(false);
    }
  };

  const handleRegisterBiometric = async () => {
    if (!deviceName.trim()) {
      toast.error('Inserisci un nome per il dispositivo');
      return;
    }

    setRegistering(true);
    try {
      const optionsRes = await webauthnAPI.getRegistrationOptions(deviceName);
      const options = JSON.parse(optionsRes.data.options);

      const publicKeyCredentialCreationOptions = {
        challenge: base64urlToUint8Array(options.challenge),
        rp: { name: options.rp.name, id: optionsRes.data.rp_id },
        user: {
          id: base64urlToUint8Array(options.user.id),
          name: options.user.name,
          displayName: options.user.displayName
        },
        pubKeyCredParams: options.pubKeyCredParams,
        timeout: options.timeout || 60000,
        authenticatorSelection: options.authenticatorSelection,
        attestation: options.attestation || 'none',
        excludeCredentials: (options.excludeCredentials || []).map(cred => ({
          id: base64urlToUint8Array(cred.id),
          type: cred.type,
          transports: cred.transports
        }))
      };

      const credential = await navigator.credentials.create({
        publicKey: publicKeyCredentialCreationOptions
      });

      if (credential) {
        const credentialJSON = {
          id: credential.id,
          rawId: arrayBufferToBase64url(credential.rawId),
          type: credential.type,
          response: {
            clientDataJSON: arrayBufferToBase64url(credential.response.clientDataJSON),
            attestationObject: arrayBufferToBase64url(credential.response.attestationObject),
            transports: credential.response.getTransports ? credential.response.getTransports() : ['internal']
          },
          authenticatorAttachment: credential.authenticatorAttachment
        };

        await webauthnAPI.verifyRegistration(credentialJSON, deviceName);
        toast.success('Impronta digitale registrata con successo!');
        setDeviceName('');
        loadCredentials();
      }
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        toast.error('Registrazione annullata');
      } else {
        toast.error(err.response?.data?.detail || 'Errore nella registrazione biometrica');
      }
    } finally {
      setRegistering(false);
    }
  };

  const handleDeleteCredential = async (credentialId) => {
    if (!window.confirm('Sei sicuro di voler rimuovere questa impronta?')) return;
    try {
      await webauthnAPI.deleteCredential(credentialId);
      toast.success('Impronta rimossa');
      loadCredentials();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella rimozione');
    }
  };

  const isGoogleOnlyAccount = !user?.has_password;

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }} data-testid="profile-title">
          Profilo
        </h1>
        <p className="text-muted-foreground mt-1">Le tue informazioni personali e sicurezza</p>
      </div>

      {/* User Info Card */}
      <Card className="border-border bg-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-4 mb-6">
            <Avatar className="w-16 h-16">
              {user?.picture && <AvatarImage src={user.picture} alt={user.name} />}
              <AvatarFallback className="bg-primary/10 text-primary text-xl">
                {user?.name?.charAt(0)?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <h2 className="text-xl font-semibold" style={{ fontFamily: 'Manrope' }} data-testid="profile-name">
                {user?.name}
              </h2>
              <Badge className={ROLE_COLORS[user?.role]} data-testid="profile-role">
                {ROLE_LABELS[user?.role]}
              </Badge>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50">
              <Mail className="w-5 h-5 text-muted-foreground flex-shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground">Email</p>
                <p className="text-sm font-medium" data-testid="profile-email">{user?.email}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50">
              <Calendar className="w-5 h-5 text-muted-foreground flex-shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground">Registrato il</p>
                <p className="text-sm font-medium" data-testid="profile-created">
                  {user?.created_at ? new Date(user.created_at).toLocaleDateString('it-IT', {
                    year: 'numeric', month: 'long', day: 'numeric'
                  }) : '-'}
                </p>
              </div>
            </div>

            {user?.subscription_status && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50">
                <UserCircle className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Abbonamento</p>
                  <p className="text-sm font-medium capitalize" data-testid="profile-subscription">
                    {user.subscription_status === 'trial' ? 'Prova Gratuita' : 'Attivo'} - {user.subscription_plan}
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* 2FA Settings Card */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Shield className="w-5 h-5 text-blue-500" />
            Autenticazione a Due Fattori (2FA)
          </CardTitle>
          <CardDescription>
            Gestisci la sicurezza del tuo account
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-lg border border-border">
            <div className="flex items-center gap-3">
              <Lock className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="font-medium">Verifica OTP via Email</p>
                <p className="text-sm text-muted-foreground">
                  {twoFAEnabled 
                    ? 'Richiedi un codice OTP ad ogni login' 
                    : 'Accedi senza codice OTP aggiuntivo'}
                </p>
              </div>
            </div>
            <Switch
              checked={twoFAEnabled}
              onCheckedChange={handleToggle2FA}
              disabled={toggling2FA}
              data-testid="2fa-toggle"
            />
          </div>

          {/* Trusted Devices */}
          <Separator />
          <div>
            <div className="flex items-center justify-between mb-3">
              <Label className="flex items-center gap-2">
                <Laptop className="w-4 h-4" />
                Dispositivi Attendibili
              </Label>
              {trustedDevices.length > 0 && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleRemoveAllTrustedDevices}
                  className="text-destructive hover:text-destructive"
                  data-testid="remove-all-devices-btn"
                >
                  Rimuovi tutti
                </Button>
              )}
            </div>
            
            {loadingDevices ? (
              <div className="flex items-center justify-center py-4">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : trustedDevices.length > 0 ? (
              <div className="space-y-2">
                {trustedDevices.map((device) => (
                  <div
                    key={device.device_id}
                    className="flex items-center justify-between p-3 rounded-lg border border-border bg-secondary/30"
                  >
                    <div className="flex items-center gap-3 overflow-hidden">
                      <Laptop className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <div className="overflow-hidden">
                        <p className="text-sm font-medium truncate">{device.device_name?.slice(0, 50) || 'Dispositivo'}</p>
                        <p className="text-xs text-muted-foreground">
                          Scade il {new Date(device.expires_at).toLocaleDateString('it-IT')}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveTrustedDevice(device.device_id)}
                      data-testid={`remove-device-${device.device_id}`}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                Nessun dispositivo attendibile. Seleziona "Ricorda questo dispositivo" al prossimo login.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Biometric Authentication Card */}
      {biometricAvailable && (
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Fingerprint className="w-5 h-5 text-blue-500" />
              Accesso Biometrico
            </CardTitle>
            <CardDescription>
              Registra la tua impronta digitale per accedere senza password e OTP
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {loadingCredentials ? (
              <div className="flex items-center justify-center py-4">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : credentials.length > 0 ? (
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Dispositivi registrati</Label>
                {credentials.map((cred) => (
                  <div
                    key={cred.credential_id}
                    className="flex items-center justify-between p-3 rounded-lg border border-border bg-secondary/30"
                  >
                    <div className="flex items-center gap-3">
                      <Fingerprint className="w-5 h-5 text-green-500" />
                      <div>
                        <p className="text-sm font-medium">{cred.device_name}</p>
                        <p className="text-xs text-muted-foreground">
                          Registrato il {new Date(cred.created_at).toLocaleDateString('it-IT')}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteCredential(cred.credential_id)}
                      data-testid={`delete-credential-${cred.credential_id}`}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                Nessuna impronta registrata
              </p>
            )}

            <Separator />

            <div className="space-y-3">
              <Label>Registra nuovo dispositivo</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Nome dispositivo (es: MacBook Pro)"
                  value={deviceName}
                  onChange={(e) => setDeviceName(e.target.value)}
                  data-testid="biometric-device-name"
                />
                <Button
                  onClick={handleRegisterBiometric}
                  disabled={registering || !deviceName.trim()}
                  data-testid="register-biometric-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {registering ? '...' : 'Registra'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Change Password Card */}
      {!isGoogleOnlyAccount && (
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Key className="w-5 h-5 text-orange-500" />
              Cambia Password
            </CardTitle>
            <CardDescription>
              Aggiorna la password del tuo account
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Password attuale</Label>
              <div className="relative">
                <Input
                  id="currentPassword"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  data-testid="current-password-input"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="newPassword">Nuova password</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  data-testid="new-password-input"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Conferma nuova password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                data-testid="confirm-password-input"
              />
            </div>

            <Button
              onClick={handleChangePassword}
              disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
              data-testid="change-password-btn"
            >
              {changingPassword ? 'Salvataggio...' : 'Cambia Password'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Delete Account Card */}
      {user?.role !== 'root' && (
        <Card className="border-destructive/50 bg-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-destructive" style={{ fontFamily: 'Manrope' }}>
              <AlertTriangle className="w-5 h-5" />
              Zona Pericolosa
            </CardTitle>
            <CardDescription>
              Azioni irreversibili sul tuo account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="p-4 rounded-lg border border-destructive/30 bg-destructive/5">
              <p className="text-sm text-muted-foreground mb-4">
                L'eliminazione dell'account e' permanente. Tutti i tuoi dati, link e ticket saranno eliminati definitivamente.
              </p>
              <Button
                variant="destructive"
                onClick={() => setShowDeleteDialog(true)}
                data-testid="delete-account-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Elimina il mio account
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delete Account Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Conferma Eliminazione Account
            </AlertDialogTitle>
            <AlertDialogDescription>
              Questa azione e' irreversibile. Tutti i tuoi dati saranno eliminati permanentemente.
              {!isGoogleOnlyAccount && ' Inserisci la tua password per confermare.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          {!isGoogleOnlyAccount && (
            <div className="py-4">
              <Label htmlFor="deletePassword">Password</Label>
              <Input
                id="deletePassword"
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="Inserisci la tua password"
                data-testid="delete-password-input"
              />
            </div>
          )}
          
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              disabled={deletingAccount || (!isGoogleOnlyAccount && !deletePassword)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-btn"
            >
              {deletingAccount ? 'Eliminazione...' : 'Elimina Account'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
